import { ContentState } from "draft-js";
import { List, Map } from "immutable";
import { WritableObservable } from "micro-observables";
import { SimpleMappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { StorePresentation } from "@core/store/storePresentation";
import { Page, PaginationOptions } from "@utils/page";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { Post } from "@domain/post/post";
import { Feedback, FeedbackInput } from "@domain/group/feedback/feedback";
import { Group, GroupAsset, GroupMemberRole, GroupType } from "./group";
import { GroupApi } from "./groupApi";
import { GroupDraft } from "./groupDraft";
import { DataChartFromDto } from "@domain/group/shareroom/dto";
import { AboutShareroomDraft, Period, ShareroomPricing } from "@domain/group/shareroom/shareroom";

export class GroupService {
	groupStore = new SimpleStore((id) => this.groupApi.get(id));
	slugGroupStore = new SimpleStore<Group>((id) => this.groupApi.getBySlug(id), { primaryKey: "slug" });
	userDiscussions = new StorePresentation(this.groupStore, (page) =>
		this.groupApi.getMyGroups("DISCUSSION_GROUP", page)
	);
	feedbackStore = new SimpleMappedStore((id, page) => this.groupApi.listFeedbacks(id, page));

	mySharerooms = new StorePresentation(this.groupStore, (page) => this.groupApi.getMyGroups("SHAREROOM", page));
	userSharerooms = new SimpleMappedStorePresentation(this.groupStore, (profileId, page) =>
		this.groupApi.getUserGroups(profileId, page)
	);
	shareroomAlias = new WritableObservable<Group | null>(null);
	constructor(
		private readonly groupApi: GroupApi,
		private readonly currentProfileService: CurrentProfileService,
		initialGroups?: Map<string, Group>
	) {
		if (initialGroups) {
			this.groupStore.merge(List(initialGroups.values()));
		}
		this.slugGroupStore.bind(this.groupStore);
	}

	async save(draft: GroupDraft, id?: string, memberIds?: string[]): Promise<Group> {
		const group = await (id ? this.groupApi.save(draft, id) : this.groupApi.create(draft, memberIds));
		this.groupStore.save(group);
		this.userDiscussions.fetch();
		return group;
	}
	async saveShareroomPricing(draft: ShareroomPricing, id: string): Promise<Group> {
		const group = await this.groupApi.saveShareroomPricing(draft, id);
		this.groupStore.save(group);
		this.userDiscussions.fetch();
		return group;
	}
	async saveShareroom(draft: AboutShareroomDraft, id: string): Promise<Group> {
		const group = await this.groupApi.saveShareroom(draft, id);
		this.groupStore.save(group);
		// this.userDiscussions.fetch();
		return group;
	}

	async deleteGroup(id: string): Promise<void> {
		await this.groupApi.delete(id);
		this.groupStore.remove(id);
	}

	async joinGroup(id: string, role: GroupMemberRole): Promise<void> {
		await this.groupApi.joinGroup(id);
		this.groupStore.update(id, (c) => ({ ...c, userRole: role, status: "VALIDATED" }));
	}

	async leaveGroup(id: string): Promise<void> {
		await this.groupApi.leaveGroup(id);
		this.updateMemberCount(id, "MEMBER", -1);
	}

	async requestMembership(id: string, isGroupOpen?: boolean): Promise<void> {
		await this.groupApi.requestMembership(id);
		this.groupStore.update(id, (c) => ({
			...c,
			userRole: "MEMBER",
			status: isGroupOpen ? "VALIDATED" : "PENDING_HOST_ACCEPTANCE",
		}));
	}

	async acceptMembership(groupId: string, userId: string, role: GroupMemberRole): Promise<void> {
		await this.groupApi.acceptMembership(groupId, userId, role);
	}

	updateMemberCount(groupId: string, role: GroupMemberRole, delta: number): void {
		this.groupStore.update(groupId, (group) => ({
			...group,
			memberCounts: { ...group.memberCounts, [role]: (group.memberCounts[role] ?? 0) + delta },
		}));
	}

	suggest = (count: number, groupType: GroupType): Promise<Page<Group>> => {
		return this.groupApi.suggest(
			{ page: 0, size: count },
			this.currentProfileService.currentProfile.get()?.categories ?? [],
			groupType
		);
	};
	getMyGroups = (
		pageOptions: PaginationOptions,
		type: GroupType,
		memberRole?: GroupMemberRole | "ALL"
	): Promise<Page<Group>> => {
		return this.groupApi.getMyGroups(type, pageOptions, memberRole);
	};

	async getActifMemberCount(groupId: string): Promise<{ members: number }> {
		return await this.groupApi.getActifMemberCount(groupId);
	}

	async getAssets(count: number, groupId: string, type: string): Promise<Page<GroupAsset>> {
		return await this.groupApi.getAssets(groupId, { page: 0, size: count }, type);
	}

	async getSubscriptionsInPeriod(groupId: string, period: Period): Promise<Array<DataChartFromDto>> {
		return this.groupApi.getSubscriptionsInPeriod(groupId, period);
	}

	async shareGroup(groupId: string, message?: ContentState, feedGroupId?: string): Promise<Post> {
		return this.groupApi.share(groupId, message, feedGroupId);
	}

	search = this.groupApi.search.bind(this.groupApi);

	getAlias(): WritableObservable<Group | null> {
		return this.shareroomAlias;
	}

	setAlias(alias: Group | null): void {
		this.shareroomAlias.set(alias);
	}

	async sendFeedback(groupId: string, feedback: FeedbackInput): Promise<void> {
		await this.groupApi.sendFeedback(groupId, feedback);
		await this.feedbackStore.fetch(groupId);
		await this.groupStore.fetch(groupId);
	}
	async deleteFeedback(groupId: string, groupFeedbackId: string): Promise<void> {
		await this.groupApi.deleteFeedback(groupId, groupFeedbackId);
		await this.feedbackStore.fetch(groupId);
		await this.groupStore.fetch(groupId);
	}

	async lastFeedbacks(groupId: string): Promise<Array<Feedback>> {
		return this.groupApi.lastFeedbacks(groupId);
	}

	async getMyFeedback(groupId: string, feedbackId: string): Promise<Feedback | null> {
		try {
			return await this.groupApi.getMyFeedback(groupId, feedbackId);
		} catch (e) {
			if (e instanceof Error && "code" in e && e.code === 404) {
				return null;
			}
			throw e;
		}
	}
}
